import { useMemo } from 'react';
import type { PackEntry } from 'types/shared';
import type { CreativesByHypothesis } from 'features/creatives';
import type { HypothesisCreativeEntry } from '../types/hypothesis';
import {
  MIXED_PACK_KEY,
  packHypothesisKeyFromPack,
  isCreativePackedUnderHypothesis,
} from '../utils/hypothesisPackUtils';

export { MIXED_PACK_KEY };

export interface HypothesisPack {
  packIndex: number;
  tag: string;
  creatives: HypothesisCreativeEntry[];
}

type HypothesisKey = string;

interface UseCreativesByPackReturn {
  packsByHypothesis: Map<HypothesisKey, HypothesisPack[]>;
  /** Same dictionary shape as the API, minus creatives already held by a pack of that hypothesis. */
  unpackedByHypothesis: CreativesByHypothesis<HypothesisCreativeEntry>;
  unpackedCreatives: HypothesisCreativeEntry[];
  packedIds: Set<string>;
}

const useCreativesByPack = (
  packsWithCreatives: PackEntry[],
  creativesByHypothesis: CreativesByHypothesis<HypothesisCreativeEntry>,
): UseCreativesByPackReturn => {
  return useMemo(() => {
    const resolveById = new Map<string, HypothesisCreativeEntry>();
    for (const creatives of Object.values(creativesByHypothesis)) {
      for (const creative of creatives) {
        resolveById.set(creative.id, creative);
      }
    }

    const packsByHypothesis = new Map<HypothesisKey, HypothesisPack[]>();
    const packedIds = new Set<string>();

    packsWithCreatives.forEach((pack, packIndex) => {
      const ownerKey = packHypothesisKeyFromPack(pack);
      const packCreatives: HypothesisCreativeEntry[] = [];

      for (const id of pack.creatives) {
        if (typeof id !== 'string') continue;
        packedIds.add(id);
        const creative = resolveById.get(id);
        if (!creative) continue;
        packCreatives.push(creative);
      }

      // A pack whose creatives are all hidden by the active filters has nothing to show.
      if (packCreatives.length === 0) return;

      const entry: HypothesisPack = {
        packIndex,
        tag: pack.tag,
        creatives: packCreatives,
      };
      const list = packsByHypothesis.get(ownerKey);
      if (list) list.push(entry);
      else packsByHypothesis.set(ownerKey, [entry]);
    });

    const unpackedByHypothesis: CreativesByHypothesis<HypothesisCreativeEntry> = {};
    const unpackedCreatives: HypothesisCreativeEntry[] = [];

    for (const [hypothesis, creatives] of Object.entries(creativesByHypothesis)) {
      const unpacked = creatives.filter(
        (creative) =>
          !isCreativePackedUnderHypothesis(
            creative.id,
            hypothesis,
            packsWithCreatives,
          ),
      );
      unpackedByHypothesis[hypothesis] = unpacked;
      unpackedCreatives.push(...unpacked);
    }

    return {
      packsByHypothesis,
      unpackedByHypothesis,
      unpackedCreatives,
      packedIds,
    };
  }, [packsWithCreatives, creativesByHypothesis]);
};

export default useCreativesByPack;
